import queryString from "query-string";

import { oneLinkBase } from "../config";

// Purpose of this script is to generate dynamic OneLink URL based on query parameters it knows about.
// The logic of `generateUrl` is explained in the linked source below, extend if needed.
// Notes about changes in file can be found inline.
//
// Original source taken from:
// https://support.appsflyer.com/hc/en-us/articles/360000677217-Mobile-landing-page-attribution-guide

export default class AppsFlyer {
  private static instance: AppsFlyer = new AppsFlyer();
  private search = "";

  setSearch(search: string) {
    this.search = search;
  }

  static getInstance(): AppsFlyer {
    return this.instance;
  }

  // Returns the value of a parameter existing in the page's URL or undefined if not exists.
  private getParameterFromURL(name: string): string | undefined {
    const queryParameters = queryString.parse(this.search);
    if (queryParameters[name] === null) {
      return undefined;
    }

    if (Array.isArray(queryParameters[name])) {
      // Despite the null check and the array check typescript still believes that `queryParameters[name]` can be null.
      // I'm quite confident it's not possible, hence the casting to string[]
      return (queryParameters[name] as string[]).length > 0
        ? (queryParameters[name] as string[])[0]
        : undefined;
    }
    return queryParameters[name] as string;
  }

  private isMobileDevice() {
    return (
      typeof window.orientation !== "undefined" || navigator.userAgent.indexOf("IEMobile") !== -1
    );
  }

  private isAFLink() {
    return this.getParameterFromURL("af_redirect");
  }
  // Note - when device ID sharing becomes optional stop calling this method (or always return false)
  private isFacebook() {
    if (document.referrer && document.referrer !== "") {
      return document.referrer.toLowerCase().includes("facebook");
    } else {
      return false;
    }
  }

  private isGoogleRetargetingCampaign(source: string, campaignName: string) {
    return source == "adwords" && campaignName.toLowerCase().includes("retargeting");
  }

  // generateUrl returns the URL to use behind the iOS and Android "Download" buttons on a landing page, based on the source of the page visitor.
  // By default these buttons should direct to the apps' pages on iTunes and Google Play.
  // If these links should be kept with no change, generateUrl returns ' '.
  // Otherwise, generateUrl returns the URL to be used under BOTH buttons (a single app download button could also be used in this case).
  // Parameters: isDebug - if true, alerts are issued for each of the cases, otherwise not.
  generateUrl(isDebug: boolean) {
    // Used for desktop users to send download links to their devices. We won't need this.
    // let webFormURL = 'https://yoursite.com/webform.html'; // **** Replace with your own web form URL for getting the user's email or SMS ****

    let finalURL = "";
    let partnerIDParam = "?pid=";

    let campaignValue;
    if (this.getParameterFromURL("af_c")) {
      campaignValue = this.getParameterFromURL("af_c");
    } else if (this.getParameterFromURL("utm_campaign")) {
      campaignValue = this.getParameterFromURL("utm_campaign");
    } else if (document.getElementsByTagName("title")[0]) {
      campaignValue = document.getElementsByTagName("title")[0].innerText;
    } else {
      campaignValue = "unknown";
    }
    let campaignParam = "&c=";
    let gclidParam = "&af_sub1=";
    const gclidValue = this.getParameterFromURL("gclid");
    let kwParam = "&af_keywords=";
    let pidValue;
    const kwValue = this.getParameterFromURL("keyword");

    if (this.getParameterFromURL("af_pid")) {
      pidValue = this.getParameterFromURL("af_pid");
    } else if (this.getParameterFromURL("utm_source")) {
      pidValue = this.getParameterFromURL("utm_source");
    }

    // Prevent the use of real SRN names. Remove this part after you are done testing the script.
    const SRNs = [
      "twitter_int",
      "facebook_int",
      "snapchat_int",
      "doubleclick_int",
      "yahoogemini_int",
      "yahoojapan_int",
    ];

    if (pidValue && SRNs.includes(pidValue)) {
      alert(
        "DO NOT USE NAMES OF SRNS IN af_pid or utm_source - use the names listed in Other SRNs: Add Parameter section in the landing page article\nhttps://support.appsflyer.com/hc/en-us/articles/360000677217#other-srns-add-parameter"
      );
      return;
    }

    // Desktop user
    if (!this.isMobileDevice()) {
      //  Desktop users who want to download an Ipad App will use the original AppStore URL.
      //  return webFormURL;
      return "";
    }

    // User was redirected using af_r parameter on an AppsFlyer attribution link
    if (this.isAFLink()) {
      if (isDebug) {
        alert(
          "his user comes from AppsFlyer by redirection and is ready to be attributed. \nKeep direct app store links."
        );
      }
      return; // in this case, the original store links in the install buttons stay the same
    }

    // Google Ads
    if (gclidValue) {
      // is_retargeting=true needs to be added to the link for google retargeting campaigns
      // in order to make appsflyer attribute it as actual retargeting
      let retargetingParam;
      if (this.isGoogleRetargetingCampaign(pidValue || "", campaignValue || "")) {
        partnerIDParam += "google_web_retargeting";
        retargetingParam = "&is_retargeting=true";
      } else {
        partnerIDParam += "google_lp";
      }

      campaignParam += campaignValue;
      gclidParam += gclidValue;
      const adSetValue = this.getParameterFromURL("utm_adgroup");
      const adSetParam = adSetValue ? `&af_adset=${adSetValue}` : "";
      if (!kwValue) {
        finalURL =
          oneLinkBase + partnerIDParam + campaignParam + gclidParam + adSetParam + retargetingParam;
        if (isDebug) {
          alert("This user comes from Google AdWords\n " + finalURL);
        }
        return finalURL;
      } else {
        // Google Ads with KW
        kwParam += kwValue;
        finalURL =
          oneLinkBase +
          partnerIDParam +
          campaignParam +
          gclidParam +
          adSetParam +
          kwParam +
          retargetingParam;
        if (isDebug) {
          alert(
            "This user comes from Google AdWords - there is a keyword associated with the ad\n " +
              finalURL
          );
        }
        return finalURL;
      }

      // Other SRNs and custom networks
    } else if (pidValue) {
      campaignParam += campaignValue;
      partnerIDParam += pidValue;
      finalURL = oneLinkBase + partnerIDParam + campaignParam;
      if (isDebug) {
        alert("This user comes the SRN or custom network " + pidValue + "\n" + finalURL);
      }
      return finalURL;
    } else if (this.isFacebook()) {
      if (isDebug) {
        alert(
          "This user comes from a paid Facebook ad - don't do anything. \nKeep direct app store links."
        );
      }
      return " ";
    } else {
      // organic mobile user
      campaignParam += campaignValue;
      partnerIDParam += "website"; //**** Replace value if you wish organic users to be attributed to another media source than 'website' ****
      finalURL = oneLinkBase + partnerIDParam + campaignParam;
      if (isDebug) {
        alert(
          "This user comes from an unknown mobile source.\n The user would be attributed to media source 'website' and to the campaign " +
            campaignParam +
            "\n" +
            finalURL
        );
      }
      return finalURL;
    }
  }
}
